/**
 * @file OrderPipelinesIndex.tsx
 * @description Заглушка-редирект для `/shop/:slug/orders/pipelines`
 * (phase10.5 шаг 8).
 *
 * Магазин этапа 1 имеет ровно один pipeline (`default_orders`), поэтому
 * отдельная страница списка не нужна — сразу ведём пользователя на редактор
 * стадий default-pipeline'а. Когда (если) появится поддержка нескольких
 * pipeline'ов — здесь заменится на полноценный список с CRUD.
 */

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { ApiError } from "../../../lib/api-client";
import { listOrderPipelines } from "../../../lib/api-orders";

export function OrderPipelinesIndex() {
  const { slug } = useParams<{ slug: string }>();
  const [targetId, setTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pipelines = await listOrderPipelines();
        if (cancelled) return;
        // Берём default + isActive, иначе первый активный, иначе первый.
        const def =
          pipelines.find((p) => p.isDefault && p.isActive) ??
          pipelines.find((p) => p.isActive) ??
          pipelines[0] ??
          null;
        if (!def) {
          setError(
            "В магазине нет ни одного pipeline'а заказов. Запустите seed (crm:seed) или создайте pipeline.",
          );
          setLoading(false);
          return;
        }
        setTargetId(def.id);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? err.message
            : "Не удалось загрузить pipeline'ы заказов",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-on-surface-variant" />
      </div>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!targetId) {
    return <Alert severity="error">Pipeline заказов не найден.</Alert>;
  }
  return (
    <Navigate
      to={`/shop/${slug}/orders/pipelines/${targetId}/stages`}
      replace
    />
  );
}
